import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, ArrowRight, Zap } from 'lucide-react';
import { Container, Btn } from '../ui';
import AstridBrainDemo from '../AstridBrainDemo';
import ConversationOverlay from './ConversationOverlay';
import { TIMELINE, getStoryState, CLUSTERS, COLORS, SCENARIOS, FOCUSED_IDS } from './scenarios';

const Hero3DCanvas = React.lazy(() => import('./Hero3DCanvas'));

const CHIPS = [
  { label: 'Investigate spend', scn: 0 },
  { label: 'Create a setup', scn: 1 },
  { label: 'Start ACH outreach', scn: 2 },
  { label: 'Assign to my team', scn: 3 },
];

function useShouldRender3D() {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const small = window.innerWidth < 900;
    let webgl = false;
    try { const c = document.createElement('canvas'); webgl = !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { webgl = false; }
    setOk(!reduced && !small && webgl);
  }, []);
  return ok;
}

function IntelligenceHUD({ story }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-[15] hidden items-center justify-center lg:flex"
      style={{ opacity: story.behind ? 1 : 0, transition: 'opacity 0.6s' }} aria-hidden>
      <div className="relative flex max-w-md flex-wrap items-center justify-center gap-2 px-6">
        {story.clusters.map((k, i) => (
          <span key={k} style={{ animation: 'astridFrag 0.5s ease-out both', animationDelay: `${i * 0.06}s`, borderColor: `${CLUSTERS[k].color}66`, boxShadow: `0 0 20px ${CLUSTERS[k].color}33` }}
            className="rounded-full border bg-[#0b1020]/80 px-3 py-1 text-[11px] font-semibold text-slate-200 capitalize backdrop-blur">{k}</span>
        ))}
        {story.fragments.map((f, i) => (
          <span key={`f${i}`} style={{ animation: 'astridFrag 0.5s ease-out both', animationDelay: `${0.2 + i * 0.08}s`, color: f.tone === 'insight' ? '#ffd79a' : '#bfe3ff', borderColor: `${f.tone === 'insight' ? COLORS.insight : COLORS.context}66` }}
            className="rounded-md border bg-[#0a0f1c]/85 px-2.5 py-1 text-[11px] font-semibold backdrop-blur">{f.text}</span>
        ))}
      </div>
    </div>
  );
}

function HeroCopy({ onAsk, onChip, activeScn }) {
  return (
    <div className="relative z-20 max-w-xl">
      <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
        <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Introducing Astrid
      </span>
      <h1 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]" data-testid="astrid-hero-positioning">
        Your AI teammate to support UBM Operations
      </h1>
      <p className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Ask. Investigate. Create. Communicate. Act.</p>
      <p className="mt-4 text-base leading-relaxed text-slate-300">
        Astrid understands your BillPortal environment, works across your data and workflows, creates reports and
        operational files, manages vendor communications, guides your team through the platform — and brings actions
        back to you for approval.
      </p>
      <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-4 py-2 text-sm font-bold text-white">
        <Zap className="h-4 w-4 text-brand" /> Drive UBM operations at 5× speed
      </div>
      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        <Btn onClick={() => onAsk('Which vendors need my attention this week?')} size="lg" data-testid="astrid-hero-try">
          <Sparkles className="h-4 w-4" /> Ask Astrid an example
        </Btn>
        <Btn href="#capabilities" size="lg" variant="outline" className="!border-white/25 !text-white hover:!bg-white/10">See what Astrid does</Btn>
      </div>
      <div className="mt-6 flex flex-wrap gap-2" data-testid="astrid-hero-chips">
        {CHIPS.map((c) => (
          <button key={c.label} onClick={() => onChip(c.scn)} data-testid={`astrid-chip-${c.scn}`}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${activeScn === c.scn ? 'border-brand bg-brand/15 text-white' : 'border-white/12 bg-white/[0.04] text-slate-300 hover:border-brand/50 hover:text-white'}`}>
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AstridHero({ onAsk }) {
  const render3D = useShouldRender3D();
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(true);
  const [i, setI] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!render3D || !inView) return;
    timer.current = setTimeout(() => setI((n) => (n + 1) % TIMELINE.length), TIMELINE[i].dur);
    return () => clearTimeout(timer.current);
  }, [i, render3D, inView]);

  const step = TIMELINE[i];
  const story = useMemo(() => getStoryState(step.scn, step.id), [step.scn, step.id]);

  const jumpTo = (scn) => {
    const idx = TIMELINE.findIndex((t) => t.scn === scn && t.id === 'ask');
    if (idx >= 0) setI(idx);
  };

  if (!render3D) {
    return (
      <section ref={sectionRef} className="relative overflow-hidden border-b border-white/10 bg-[#06070d] px-5 py-14 sm:px-8 sm:py-16">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(50rem 30rem at 80% -10%, rgba(255,92,92,0.14), transparent 60%)' }} />
        <Container className="relative">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <HeroCopy onAsk={onAsk} onChip={() => {}} activeScn={-1} />
            <div className="relative z-20"><AstridBrainDemo /></div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="relative overflow-hidden border-b border-white/10 bg-[#06070d]" style={{ minHeight: 700 }} data-testid="astrid-hero-3d">
      <Suspense fallback={<div className="absolute inset-0 bg-[#06070d]" />}>
        <Hero3DCanvas story={story} active={inView} />
      </Suspense>
      <div className="pointer-events-none absolute inset-0 z-10" style={{ background: 'linear-gradient(90deg, rgba(6,7,13,0.94) 0%, rgba(6,7,13,0.6) 40%, rgba(6,7,13,0) 64%)' }} />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-24" style={{ background: 'linear-gradient(0deg, #06070d, transparent)' }} />
      <IntelligenceHUD story={story} />
      <Container className="relative z-20">
        <div className="grid min-h-[700px] grid-cols-1 items-center gap-10 py-16 lg:grid-cols-2">
          <HeroCopy onAsk={onAsk} onChip={jumpTo} activeScn={step.scn} />
          <div className="pointer-events-none flex justify-center lg:justify-end">
            <div className="pointer-events-auto w-full max-w-[440px]">
              <ConversationOverlay scn={step.scn} id={step.id} />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
